import * as React from 'react';
import { action, observable } from 'mobx';
import { observer } from 'mobx-react';
import Downshift, {
    DownshiftState,
    StateChangeOptions,
    ControllerStateAndHelpers
} from 'downshift';
import computeScrollIntoView from 'compute-scroll-into-view';
import * as classnames from 'classnames';
import styled, { css, StyledComponentBase } from 'styled-components';
import { ArrowIcon } from '@estee/elc-icons';
import { InputWrapper, FloatingLabelInput } from '@estee/elc-inputs';
import { ContentSpacing } from '@estee/elc-base-theme';
import { IOptionItem } from '../interfaces/IDropDownItem';
import { IDropDownFieldProps } from '../interfaces/IDropDownFieldProps';
import { DropdownItems } from './DropdownItems';
import {
    dropdownContainerTheme,
    dropdownArrowButtonTheme,
    dropdownInputWrapperTheme,
    labelTheme,
    dropdownDisabledTheme,
    iconDisabledTheme
} from '../theme/default-theme';

interface IStyledInput {
    className?: string;
}

const DropdownWrapper: StyledComponentBase<'input', {}> & IStyledInput = styled(InputWrapper)`
    width: 100%;
`;

const DropdownContainer = styled.div`
    ${dropdownContainerTheme};
`;

const Label = styled.label`
    cursor: pointer;
    text-overflow: ellipsis;
    white-space: nowrap;
    padding: 0 ${ContentSpacing.space8};
    ${labelTheme};
`;

const IconContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 ${ContentSpacing.space8};
`;

const Button = styled.button<{ disabled: boolean }>`
    background-color: transparent;
    border: none;
    border-left-width: 1px;
    border-left-style: solid;
    cursor: pointer;
    height: 40px;
    padding: 0 ${ContentSpacing.space16};
    ${dropdownArrowButtonTheme};
    ${({ disabled }) =>
        disabled &&
        css`
            ${dropdownDisabledTheme} ${iconDisabledTheme};
        `};

    [dir='rtl'] & {
        border-left: none;
        border-right-width: 1px;
        border-right-style: solid;
    }
`;

const DropdownInputWrapper = styled.div<{ isDisabled: boolean }>`
    border: 1px solid;
    cursor: pointer;
    display: flex;
    align-items: center;
    padding-left: ${ContentSpacing.space8};
    position: relative;
    ${dropdownInputWrapperTheme};
    ${({ isDisabled }) =>
        isDisabled &&
        css`
            ${dropdownDisabledTheme};
        `};

    [dir='rtl'] & {
        padding-left: 0;
        padding-right: ${ContentSpacing.space8};
    }
`;

const ReadonlyInput = styled.div`
    display: flex;
    align-items: center;
    flex: 1;
    font-size: 14px;
    overflow: hidden;
    padding: 0 ${ContentSpacing.space8};
    text-overflow: ellipsis;
    white-space: nowrap;
`;

// cannot use input as styled component
// issue open: https://github.com/downshift-js/downshift/issues/718
@observer
export class Dropdown extends React.Component<IDropDownFieldProps> {
    @observable private searchAheadValue: string = '';
    private timeout: number;
    private dropdownListRef: React.RefObject<HTMLDivElement> = React.createRef();

    public componentDidMount() {
        this.setDropdownRoleSafari();
    }

    public onInputChangeHandle = (inputValue: string) => {
        if (inputValue === '' && !this.props.allowCustomInput && !this.props.isFilterable) {
            return;
        }
        this.props.onChange(this.props.name, inputValue);
    };

    public outerClickHandler = () => {
        if (this.props.dropdownClickOutside) {
            this.props.dropdownClickOutside(this.props.name);
        }
    };

    @action
    private enableSearchAhead = (
        updatehighlightedIndex: (index: number | null) => void,
        highlightedIndex: number | null
    ) => {
        window.onkeydown = (e: KeyboardEvent) => {
            if (!this.searchAheadValue) {
                this.timeout = window.setTimeout(() => {
                    this.clearSearchAhead();
                }, 2000);
            }
            this.searchAheadValue += e.key;

            const first = this.props.options.findIndex(
                this.optionStartsWithSearchText(this.searchAheadValue)
            );
            if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                updatehighlightedIndex(highlightedIndex);
                this.clearSearchAhead();
            } else {
                updatehighlightedIndex(first);
            }
        };
    };

    @action
    private disableSearchAhead = () => {
        window.onkeydown = null;
        clearTimeout(this.timeout);
        this.clearSearchAhead();
        this.props.onChange(this.props.name, this.props.selectedValue);
    };

    @action
    public clearSearchAhead = () => {
        this.searchAheadValue = '';
    };

    private getLabel = (value: string | null): string => {
        if (value === null) {
            return '';
        }
        const selectedItem = this.getItems().find((item: IOptionItem) => item.value === value);

        return selectedItem ? selectedItem.label : value;
    };

    private optionStartsWithSearchText = (filterBy: string) => (item: IOptionItem) =>
        item.label.toLowerCase().indexOf(filterBy.toLowerCase()) === 0;

    private getItems(filterBy: string = '') {
        const { options, isFilterable } = this.props;

        return isFilterable && filterBy
            ? options.filter(this.optionStartsWithSearchText(filterBy))
            : options;
    }

    private scrollIntoView = (node: HTMLElement) => {
        if (node) {
            const actions = computeScrollIntoView(node, {
                scrollMode: 'always',
                block: 'start',
                inline: 'start',
                boundary: this.dropdownListRef.current
            });
            actions.forEach(({ el, top, left }) => {
                el.scrollTop = top;
                el.scrollLeft = left;
            });
        }
    };

    private multiSelectionStateReducer = (
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        state: DownshiftState<any>,

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        changes: StateChangeOptions<any>
    ) => {
        let updatedChanges = changes;
        // keep dropdown open
        if (changes.type === Downshift.stateChangeTypes.clickItem) {
            const { isOpen, ...restChanges } = changes;
            updatedChanges = restChanges;
        }

        // selection should not change when clicking outside
        if (changes.type === Downshift.stateChangeTypes.mouseUp) {
            const { inputValue, ...restChanges } = changes;
            updatedChanges = restChanges;
        }

        return { ...state, ...updatedChanges };
    };

    handleStateChange = (
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        changes: StateChangeOptions<any>,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        stateAndHelpers: ControllerStateAndHelpers<any>
    ) => {
        const { searchAhead } = this.props;
        if (searchAhead) {
            if (stateAndHelpers.isOpen) {
                this.enableSearchAhead(
                    stateAndHelpers.setHighlightedIndex,
                    stateAndHelpers.highlightedIndex
                );
            } else {
                this.disableSearchAhead();
            }
        }
    };

    private get placeholder() {
        const { placeholder, isRequired, withFloatingLabel } = this.props;

        return placeholder ? `${placeholder}${!withFloatingLabel && isRequired ? '*' : ''}` : '';
    }

    private getAriaLabelBtn = (isOpen: boolean) => {
        const { withNameInAriaLabel, name } = this.props;
        if (withNameInAriaLabel) {
            return isOpen ? `close ${name} menu` : `open ${name} menu`;
        } else {
            return isOpen ? 'close menu' : 'open menu';
        }
    };

    // Safari/Voiceover a11y bug workaround
    private setDropdownRoleSafari = (): void => {
        const combobox = document.querySelector('[role="combobox"]');
        const dropdown = combobox && combobox.getAttribute('aria-labelledby');
        const isDownshift = RegExp('downshift').test(dropdown as string);
        const safariCheck = RegExp(/^((?!chrome).)*safari/);
        const isSafari = safariCheck.test(window.navigator.userAgent.toLowerCase());
        if (isSafari && combobox && isDownshift) {
            combobox.removeAttribute('role');
        }
    };

    private getDropdownComponent = () => {
        const props = this.props;

        const {
            isDisabled = false,
            dropdownIcon,
            keepItOpenOnSelection,
            withFloatingLabel,
            autoComplete
        } = props;

        const className: string = classnames(
            'elc-dropdown',
            'js-dropdown',
            props.className,
            props.customClassName,
            {
                'elc-dropdown-disabled js-dropdown-disabled': isDisabled
            }
        );
        const dropdownWrapperClassNames = classnames('elc-dropdown-wrapper', 'js-dropdown-wrapper');
        const dropdownInputWrapperClassNames = classnames(
            'elc-dropdown-input-wrapper',
            'js-dropdown-input-wrapper'
        );
        const showInput = (props.allowCustomInput || props.isFilterable) && !props.isDisabled;

        return (
            <DropdownWrapper className={dropdownWrapperClassNames}>
                <Downshift
                    onInputValueChange={this.onInputChangeHandle}
                    inputValue={props.selectedValue}
                    scrollIntoView={this.scrollIntoView}
                    initialIsOpen={this.props.isOpenByDefault}
                    onOuterClick={this.outerClickHandler}
                    stateReducer={
                        (keepItOpenOnSelection && this.multiSelectionStateReducer) || undefined
                    }
                    onStateChange={this.handleStateChange}
                    selectedItem={props.selectedValue}
                >
                    {({
                        getInputProps,
                        getLabelProps,
                        getItemProps,
                        getToggleButtonProps,
                        isOpen,
                        inputValue,
                        toggleMenu,
                        clearSelection,
                        highlightedIndex,
                        getMenuProps
                    }) => {
                        const selectedLabel = this.getLabel(inputValue);
                        const selectedItem = this.getItems().find(
                            (item: IOptionItem) => item.value === inputValue
                        );

                        const htmlContent =
                            selectedItem && selectedItem.htmlContent
                                ? selectedItem.htmlContent
                                : null;
                        const hasHtmlContent = htmlContent ? true : false;

                        const toggleDropDownMenu = () => {
                            if (!isDisabled) {
                                toggleMenu();
                            }
                        };

                        const onDropdownClick = (event: React.MouseEvent<HTMLElement>) => {
                            event.preventDefault();
                            if (props.isFilterable) {
                                clearSelection();
                            }
                            toggleDropDownMenu();
                        };

                        const readonlyInputClass = classnames(
                            this.props.customReadOnlyClass,
                            'elc-dropdown-readonly-input',
                            'js-dropdown-readonly-input',
                            'notranslate',
                            {
                                'elc-dropdown-readonly-error js-dropdown-readonly-error':
                                    props.hasError
                            }
                        );

                        const labelClass = classnames('elc-dropdown-label', 'js-dropdown-label', {
                            'elc-dropdown-label-error js-dropdown-label-error': props.hasError
                        });

                        const arrowButtonIsTabable = props.isFilterable ? -1 : 0;

                        const dropdownInput = (
                            <input
                                {...getInputProps({
                                    placeholder: '',
                                    name: props.name,
                                    value: selectedLabel,
                                    className: 'elc-dropdown-input js-dropdown-input',
                                    title: selectedLabel
                                })}
                                data-test-id={props.dataTestId}
                                style={{
                                    border: 0,
                                    height: 40,
                                    padding: `0 ${ContentSpacing.space8}`,
                                    outline: 0
                                }}
                                autoComplete={autoComplete}
                            />
                        );
                        const dropdownReadonlyInput = (
                            <ReadonlyInput className={readonlyInputClass}>
                                <input
                                    type="hidden"
                                    name={props.name}
                                    data-test-id={props.dataTestId}
                                    value={selectedLabel}
                                    title={selectedLabel}
                                />
                                {selectedLabel}
                            </ReadonlyInput>
                        );

                        return (
                            <div>
                                <DropdownContainer className={className}>
                                    <DropdownInputWrapper
                                        data-test-id={props.dropdownDataTestId}
                                        // TODO: dropdownWrapperClassNames should be removed once all react services are refactored to use the new classname
                                        className={`${dropdownWrapperClassNames} ${dropdownInputWrapperClassNames}`}
                                        onClick={toggleDropDownMenu}
                                        isDisabled={isDisabled}
                                    >
                                        {props.icon && !dropdownIcon && (
                                            <IconContainer className="elc-dropdown-icon-container">
                                                {props.icon}
                                            </IconContainer>
                                        )}
                                        {this.placeholder && !withFloatingLabel && (
                                            <Label
                                                {...getLabelProps({
                                                    className: labelClass
                                                })}
                                            >
                                                {this.placeholder}
                                                {props.selectedValue && ':'}
                                            </Label>
                                        )}
                                        {hasHtmlContent
                                            ? htmlContent
                                            : showInput
                                            ? dropdownInput
                                            : dropdownReadonlyInput}
                                        <Button
                                            disabled={isDisabled}
                                            tabIndex={arrowButtonIsTabable}
                                            {...getToggleButtonProps({
                                                className: 'elc-dropdown-arrow js-dropdown-arrow',
                                                onClick: onDropdownClick,
                                                'aria-label': this.getAriaLabelBtn(isOpen),
                                                'aria-activedescendant': getInputProps()[
                                                    'aria-activedescendant'
                                                ],
                                                'aria-expanded': isOpen
                                            })}
                                        >
                                            {dropdownIcon ? (
                                                dropdownIcon
                                            ) : (
                                                <ArrowIcon
                                                    isOpen={isOpen}
                                                    className="elc-dropdown-arrow-icon"
                                                />
                                            )}
                                        </Button>
                                    </DropdownInputWrapper>
                                    {isOpen ? (
                                        <DropdownItems
                                            ref={this.dropdownListRef}
                                            getItemProps={getItemProps}
                                            items={this.getItems(selectedLabel)}
                                            activeItem={selectedItem}
                                            highlightedIndex={highlightedIndex}
                                            useDropup={props.useDropup}
                                            getMenuProps={getMenuProps}
                                        />
                                    ) : null}
                                </DropdownContainer>
                            </div>
                        );
                    }}
                </Downshift>
            </DropdownWrapper>
        );
    };

    public render() {
        if (this.props.withFloatingLabel) {
            return (
                <FloatingLabelInput
                    inputLabel={this.placeholder}
                    markAsError={this.props.hasError}
                    isRequired={this.props.isRequired}
                    moveUp={Boolean(this.props.selectedValue)}
                >
                    {this.getDropdownComponent()}
                </FloatingLabelInput>
            );
        }

        return <>{this.getDropdownComponent()}</>;
    }
}
